import { IHttpInterceptor, IHttpInterceptorFactory, IHttpResponse, IPromise, IQService, IRequestConfig, IWindowService } from "angular";
import { IStatsItem } from "@webapp/core/tracing/models/tracing.models";
import { generateStatsItem } from "./util";

interface IStatsRequestConfig extends IRequestConfig {
  // eslint-disable-next-line @typescript-eslint/naming-convention
  __stats?: { index: number; start: number };
}

class StatsObject {
  items: IStatsItem[] = [];

  aggregate() {
    return this.items.reduce((all, item) => {
      const byScreenName = all[item.screenName] || {};
      const byScreenID = byScreenName[item.screenID] || { __total: 0 };
      byScreenName[item.screenID] = byScreenID;

      const actionName = item.actionName || "no_action";
      const byActionName = byScreenID[actionName] || {};
      byScreenID[actionName] = byActionName;

      const actionID = item.actionID || "no_action";
      const currentTime: number = byActionName[actionID] || 0;

      byActionName[actionID] = currentTime + item.time;
      byScreenID["__total"] += item.time;

      return { ...all, [item.screenName]: byScreenName };
    }, {});
  }
}

/**
 * Collects statistics for time-to-complete of each request and
 * injects a global variable into the window object to easily
 * access this statistics.
 */
export class InBrowserStatsInterceptor implements IHttpInterceptor {
  private stats: StatsObject;

  constructor($window: IWindowService, private $q: IQService) {
    this.stats = new StatsObject();
    $window.gtmhubTracingStats = this.stats;
  }

  public request = (config: IStatsRequestConfig): IRequestConfig | IPromise<IRequestConfig> => {
    const headers = config.headers || {};
    const jaegerBaggage: string = headers["jaeger-baggage"];
    const jaegerBaggageScreen: string = headers["jaeger-baggage-screen"];

    if (jaegerBaggage && jaegerBaggageScreen) {
      const item = generateStatsItem(jaegerBaggage, jaegerBaggageScreen, config);
      const length = this.stats.items.push(item);

      // correlation between the request and its response
      config.__stats = { index: length - 1, start: performance.now() };
    }

    return config;
  };

  public response = <T>(response: IHttpResponse<T>): IPromise<IHttpResponse<T>> | IHttpResponse<T> => {
    this.setTime(response);
    return response;
  };

  public responseError = <T>(rejection: IHttpResponse<T>): IPromise<IHttpResponse<T>> | IHttpResponse<T> => {
    this.setTime(rejection);
    return this.$q.reject(rejection);
  };

  private setTime = <T>(response: IHttpResponse<T>): void => {
    const config: IStatsRequestConfig = response.config;
    if (config.__stats) {
      const { index, start } = config.__stats;
      const end = performance.now();
      this.stats.items[index].time = end - start;
    }
  };

  public static factory(): IHttpInterceptorFactory {
    const factory = ($window: IWindowService, $q: IQService) => new InBrowserStatsInterceptor($window, $q);
    factory.$inject = ["$window", "$q"];
    return factory;
  }
}
